<script setup lang="ts">
import { mainMenu } from '../config/menu'
import iconMiniProgram from '~/assets/scss/static/nav/nav-miniprogram@3x.png'

const { isLogged, isActive, handleLogout } = useNavbar()
const userStore = useUserStore()
const { user } = storeToRefs(userStore)
const hoverPath = ref('')

function isExternalLink(path: string) {
  return path.startsWith('http://') || path.startsWith('https://')
}

function getMenuLink(item: { path: string; children?: { path: string }[] }, childPath = '') {
  return `${item.path}${childPath}`
}

function getMenuIcon(item: { path: string; icon?: string; activeIcon?: string }) {
  if (!item.icon)
    return ''
  const shouldUseActiveIcon = isActive(item.path) || hoverPath.value === item.path
  return shouldUseActiveIcon && item.activeIcon ? item.activeIcon : item.icon
}
</script>

<template>
  <nav class="navbar">
    <div class="navbar__menu">
      <div
        v-for="(item, index) in mainMenu"
        :key="index"
        class="navbar__item"
        :class="{ 'navbar__item--active': isActive(item.path) }"
        @mouseenter="hoverPath = item.path"
        @mouseleave="hoverPath = ''"
      >
        <template v-if="!item.children || item.children.length === 0">
          <a
            v-if="isExternalLink(item.path)"
            :href="item.path"
            class="navbar__link"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              v-if="item.icon && typeof item.icon === 'string'"
              :src="getMenuIcon(item)"
              class="navbar__icon"
              alt=""
            />
            <ElIcon v-else-if="item.icon" class="navbar__icon-el">
              <component :is="item.icon" />
            </ElIcon>
            <span>{{ item.name }}</span>
          </a>
          <NuxtLink v-else :to="item.path" class="navbar__link">
            <img
              v-if="item.icon && typeof item.icon === 'string'"
              :src="getMenuIcon(item)"
              class="navbar__icon"
              alt=""
            />
            <ElIcon v-else-if="item.icon" class="navbar__icon-el">
              <component :is="item.icon" />
            </ElIcon>
            <span>{{ item.name }}</span>
          </NuxtLink>
        </template>
        <ElDropdown v-else :hide-on-click="false" trigger="hover">
          <span class="navbar__link navbar__link--dropdown">
            <img
              v-if="item.icon && typeof item.icon === 'string'"
              :src="getMenuIcon(item)"
              class="navbar__icon"
              alt=""
            />
            <ElIcon v-else-if="item.icon" class="navbar__icon-el">
              <component :is="item.icon" />
            </ElIcon>
            <span>{{ item.name }}</span>
            <ElIcon class="navbar__arrow"><ElIconArrowDown /></ElIcon>
          </span>
          <template #dropdown>
            <ElDropdownMenu>
              <ElDropdownItem
                v-for="(child, childIndex) in item.children"
                :key="childIndex"
              >
                <NuxtLink :to="getMenuLink(item, child.path)" class="navbar__dropdown-link">
                  {{ child.name }}
                </NuxtLink>
              </ElDropdownItem>
            </ElDropdownMenu>
          </template>
        </ElDropdown>
      </div>
    </div>

    <div class="navbar__actions">
      <div class="navbar__wechat">
        <ElPopover title="扫一扫打开微信小程序" width="200" placement="bottom">
          <template #reference>
            <span class="navbar__wechat-reference">
              <!-- <img :src="iconMiniProgram" alt="" class="navbar__wechat-icon" /> -->
              <span class="navbar__wechat-text">微信小程序</span>
            </span>
          </template>
          <!-- <img
            :src="useOssImage('/img/qrcode.png')"
            alt="微信小程序二维码"
            class="navbar__qrcode"
          /> -->
              <img
            src="https://xianmao-equity.oss-cn-guangzhou.aliyuncs.com/directGoods/1750c794-998c-4048-a442-5ea729adc647.png"
            alt="微信小程序二维码"
            class="navbar__qrcode"
          />
        </ElPopover>
      </div>

      <div v-if="isLogged" class="navbar__user user-info">
        <div class="user-info__trigger">
          <ElAvatar
            :size="30"
            src="https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png"
          />
          <!-- <span class="user-info__center">用户中心</span> -->
          <span class="user-info__name">{{ user?.userName }}</span>
          <ElIcon class="user-info__arrow"><ElIconCaretTop /></ElIcon>
        </div>
        <div class="user-info__panel">
          <div class="user-info__header">
            <NuxtLink to="/user" class="user-info__profile">
              <ElAvatar
                :size="40"
                src="https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png"
              />
              <div>
                <p class="user-info__profile-name">{{ user?.userName }}</p>
                <p class="user-info__profile-code">编号:{{ user?.secretKeyCode }}</p>
              </div>
            </NuxtLink>
            <ElButton type="primary" plain size="default" round @click="handleLogout()">
              退出登录
            </ElButton>
          </div>
          <div class="user-info__body">
            <NuxtLink to="https://client.xianlaib.com/user" class="user-info__client" target="_blank">
              <img :src="useOssImage('/img/user/panel_4.png')" alt="" class="user-info__client-icon" />
              <span>进入帮充管理端</span>
              <ElIcon size="14"><ElIconArrowRightBold /></ElIcon>
            </NuxtLink>
            <div class="user-info__shortcuts" @click="navigateTo('/user')">
              <NuxtLink to="/recharge" class="user-info__shortcut">
                <img :src="useOssImage('/img/user/panel_1.png')" alt="" />
                <span>话费帮充</span>
              </NuxtLink>
              <NuxtLink to="/user/withdrawal" class="user-info__shortcut">
                <img :src="useOssImage('/img/user/panel_2.png')" alt="" />
                <span>余额提现</span>
              </NuxtLink>
              <NuxtLink to="/user/funds" class="user-info__shortcut">
                <img :src="useOssImage('/img/user/panel_3.png')" alt="" />
                <span>资金明细</span>
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>

      <NuxtLink v-else to="/login" class="navbar__login-btn">
        登录
      </NuxtLink>
    </div>
  </nav>
</template>

<style lang="scss" scoped>
$nav-orange: #FF7A3C;
$nav-text: #1f2937;
$nav-text-muted: #4b5563;
$nav-active: #FF7A3C;

.navbar {
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: space-between;
  gap: 28px;
  height: 100%;
  min-width: 0;

  &__menu {
    display: flex;
    align-items: center;
    gap: 2px;
    flex: 1;
    justify-content: flex-end;
    min-width: 0;
  }

  &__item {
    padding: 0 10px;
    height: 54px;
    display: flex;
    align-items: stretch;

    :deep(.el-dropdown) {
      height: 100%;
      display: flex;
      align-items: stretch;
    }

    :deep(.el-dropdown .el-tooltip__trigger) {
      height: 100%;
      display: inline-flex !important;
      align-items: center;
    }
  }

  &__item--active &__link::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 2px;
    background: $nav-active;
    border-radius: 2px 2px 0 0;
  }

  &__icon {
    width: 16px;
    height: 16px;
    object-fit: contain;
    flex-shrink: 0;
  }

  &__icon-el {
    font-size: 16px;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &__link {
    position: relative;
    display: inline-flex;
    align-items: center;
    height: 100%;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
    color: $nav-text-muted;
    text-decoration: none;
    cursor: pointer;
    line-height: 1;
    transition: color 0.2s;

    .navbar__item:hover &,
    .navbar__item--active & {
      color: $nav-orange;
    }

    &--dropdown {
      outline: none;
    }
  }

  &__arrow {
    font-size: 12px;
    color: #999;

    .navbar__item:hover &,
    .navbar__item--active & {
      color: $nav-orange;
    }
  }

  &__dropdown-link {
    display: block;
    color: $nav-text;
    text-decoration: none;
    width: 100%;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }

  &__wechat {
    padding-left: 14px;
    border-left: 1px solid #edf0f5;
  }

  &__wechat-text {
    font-size: 13px;
    font-weight: 500;
    color: $nav-text-muted;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: $nav-orange;
    }
  }

  &__wechat-reference {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;
  }

  &__wechat-icon {
    width: 16px;
    height: 16px;
    object-fit: contain;
    flex-shrink: 0;
  }

  &__qrcode {
    width: 100%;
    display: block;
  }

  &__login-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 72px;
    height: 26px;
    padding: 0 16px;
    background: linear-gradient(90deg, #FFB088 0%, #FF7A3C 100%);
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    border-radius: 999px;
    box-shadow: none;
    transition: transform 0.2s;

    &:hover {
      color: #fff;
      transform: translateY(-1px);
      box-shadow: none;
    }
  }
}

.user-info {
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    right: 0;
    width: 300px;
    height: 14px;
    display: none;
    z-index: 999;
  }

  &:hover::after {
    display: block;
  }

  &__trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    height: 36px;
    padding: 0 10px 0 4px;
    border: 1px solid #eef1f5;
    border-radius: 999px;
    background: #fff;
    box-shadow: none;
    transition: border-color 0.2s;
  }

  &__center {
    font-size: 14px;
    font-weight: 600;
    color: $nav-text;
  }

  &__name {
    font-size: 13px;
    color: #7b8794;
    max-width: 76px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__arrow {
    font-size: 12px;
    color: #999;
    transition: transform 0.3s;
  }

  &__panel {
    position: absolute;
    top: calc(100% + 12px);
    right: 0;
    width: 300px;
    background: #fff;
    box-shadow: 0 18px 38px rgba(15, 23, 42, 0.12);
    border: 1px solid #f0f2f5;
    border-radius: 12px;
    padding: 14px;
    z-index: 1000;
    display: none;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 12px;
    border-bottom: 1px solid #f1f1f1;
  }

  &__profile {
    display: flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
    color: inherit;
  }

  &__profile-name {
    margin: 0;
    font-size: 14px;
    line-height: 1.4;
    color: $nav-text;
  }

  &__profile-code {
    margin: 0;
    font-size: 12px;
    color: #4a6a95;
    line-height: 1.4;
  }

  &__client {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 40px;
    color: $nav-text;
    text-decoration: none;
    font-size: 14px;
  }

  &__client-icon {
    width: 20px;
    height: 20px;
  }

  &__shortcuts {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
    padding: 0 4px;
  }

  &__shortcut {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 70px;
    text-decoration: none;
    color: #0e2036;
    font-size: 13px;

    img {
      width: 40px;
      height: 40px;
      margin-bottom: 4px;
    }
  }

  &:hover &__panel {
    display: block;
  }

  &:hover &__trigger {
    // border-color: rgba(255, 106, 0, 0.32);
    box-shadow: none;
  }

  &:hover &__arrow {
    transform: rotate(180deg);
  }
}
</style>
